import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { FeedbackAnalytics } from '@/types/feedback';
import { netSentiment, featuresPerDay } from '@/lib/trend';
import { Sparkline } from './charts/Sparkline';
import { DeltaBadge } from './DeltaBadge';

/**
 * Top-of-dashboard KPI strip: total responses, avg rating, net sentiment and
 * features tracked. Each shows a small sparkline (period shape) and a delta vs
 * the immediately preceding period.
 */
export function KpiStrip({
  analytics,
  prevAnalytics,
}: {
  analytics: FeedbackAnalytics;
  prevAnalytics?: FeedbackAnalytics;
}) {
  const total = analytics.totalFeedback;
  const net = netSentiment(analytics.votes.up, analytics.votes.down, total);

  // Per-day series for the sparklines (from the account-wide daily breakdown).
  const countSeries = analytics.dailyBreakdown.map((d) => d.count);
  const ratingSeries = analytics.dailyBreakdown.filter((d) => d.count > 0).map((d) => d.averageRating);
  const netSeries = analytics.dailyBreakdown
    .filter((d) => d.count > 0)
    .map((d) => Math.round(((d.thumbsUp - d.thumbsDown) / d.count) * 100));
  const featureSeries = featuresPerDay(analytics.perFeatureDaily);

  // Deltas vs the previous period (undefined until the prior window loads).
  const prevNet = prevAnalytics
    ? netSentiment(prevAnalytics.votes.up, prevAnalytics.votes.down, prevAnalytics.totalFeedback)
    : undefined;
  const totalDelta = prevAnalytics ? total - prevAnalytics.totalFeedback : undefined;
  const ratingDelta = prevAnalytics
    ? Math.round((analytics.averageRating - prevAnalytics.averageRating) * 10) / 10
    : undefined;
  const netDelta = prevNet !== undefined ? net - prevNet : undefined;
  const featuresDelta = prevAnalytics ? analytics.featuresTracked - prevAnalytics.featuresTracked : undefined;

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <Kpi
        title="Total responses"
        value={total.toLocaleString()}
        series={countSeries}
        delta={totalDelta}
      />
      <Kpi
        title="Avg rating"
        value={`★ ${analytics.averageRating.toFixed(1)}`}
        series={ratingSeries}
        min={1}
        max={5}
        delta={ratingDelta}
        deltaFormat={(n) => `${n > 0 ? '+' : ''}${n.toFixed(1)}`}
      />
      <Kpi
        title="Net sentiment"
        value={`${net > 0 ? '+' : ''}${net}%`}
        series={netSeries}
        min={-100}
        max={100}
        stroke={net >= 0 ? 'var(--chart-1, #14b8a6)' : 'var(--destructive, #ef4444)'}
        delta={netDelta}
        deltaFormat={(n) => `${n > 0 ? '+' : ''}${n} pts`}
      />
      <Kpi
        title="Features tracked"
        value={analytics.featuresTracked.toLocaleString()}
        series={featureSeries}
        delta={featuresDelta}
      />
    </div>
  );
}

function Kpi({
  title,
  value,
  series,
  min,
  max,
  stroke,
  delta,
  deltaFormat,
}: {
  title: string;
  value: string;
  series: number[];
  min?: number;
  max?: number;
  stroke?: string;
  delta?: number;
  deltaFormat?: (n: number) => string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-between gap-2">
          <div className="text-2xl font-bold">{value}</div>
          <Sparkline values={series} min={min} max={max} stroke={stroke} aria-label={`${title} trend`} />
        </div>
        {delta !== undefined && <DeltaBadge delta={delta} format={deltaFormat} className="mt-1" />}
      </CardContent>
    </Card>
  );
}
